import React, { useState } from "react";
import axios from "axios";
import * as Icons from "lucide-react";
import { Mail, Phone, MapPin, MessageCircle, CheckCircle2, PhoneCall, ThumbsUp, ExternalLink, CalendarDays, ArrowRight } from "lucide-react";
import { toast } from "../components/ui/sonner";
import { Seo } from "../components/Seo";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { CONTACT, FINAL_CTA, SOCIALS, BOOKING_URL } from "../mock";
import { Reveal } from "../components/Shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", company: "", fleetSize: "", message: "" };

const Contact = () => {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(null);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const er = {};
    if (!form.name.trim()) er.name = "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = "Please enter a valid email.";
    if (!form.company.trim()) er.company = "Please enter your company.";
    if (!form.fleetSize) er.fleetSize = "Please select your fleet size.";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/demo-requests`, form);
      setSubmitted(data);
      toast.success("Request received — we’ll reply within 24h.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join(", ") : detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo title="Contact Fleetroot — Request a demo" description="Tell us what you move and how many vehicles. A Fleetroot product specialist replies within 24 hours with a tailored walkthrough." />
      <section className="pt-20 pb-24 md:pt-28">
        <div className="container-x grid lg:grid-cols-[1fr_1.1fr] gap-14">
          <div>
            <span className="eyebrow-pill fade-up">{CONTACT.eyebrow}</span>
            <h1 className="h-display mt-6 fade-up delay-1">{CONTACT.title}</h1>
            <p className="mt-6 text-lg md:text-xl text-[var(--fr-ink-2)] leading-relaxed fade-up delay-2">{CONTACT.text}</p>

            <ul className="mt-10 space-y-4 fade-up delay-3">
              <li className="flex items-center gap-3 text-[15px]">
                <span className="w-9 h-9 rounded-full bg-[var(--fr-primary-tint)] text-[var(--fr-primary-dark)] flex items-center justify-center">
                  <Mail size={16} />
                </span>
                <a href={`mailto:${CONTACT.email}`} className="hover:text-[var(--fr-primary-dark)]">
                  {CONTACT.email}
                </a>
              </li>
              <li className="flex items-center gap-3 text-[15px]">
                <span className="w-9 h-9 rounded-full bg-[var(--fr-primary-tint)] text-[var(--fr-primary-dark)] flex items-center justify-center">
                  <MapPin size={16} />
                </span>
                <a
                  href={CONTACT.mapUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 hover:text-[var(--fr-primary-dark)]"
                  data-testid="contact-map-link"
                >
                  {CONTACT.address} <ExternalLink size={13} className="text-[var(--fr-muted)]" />
                </a>
              </li>
              <li>
                <a
                  href={FINAL_CTA.whatsapp}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-outline mt-2"
                  data-testid="contact-whatsapp"
                >
                  <MessageCircle size={16} className="text-[var(--fr-whatsapp)]" /> Chat on WhatsApp
                </a>
              </li>
            </ul>

            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noreferrer"
              className="card card-hover mt-8 p-5 flex items-center gap-4 group"
              data-testid="contact-book-call"
            >
              <span className="w-11 h-11 rounded-xl bg-[var(--fr-primary-tint)] text-[var(--fr-primary-dark)] flex items-center justify-center flex-shrink-0">
                <CalendarDays size={19} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block font-display font-bold">Prefer to pick a time?</span>
                <span className="block text-sm text-[var(--fr-ink-2)] mt-0.5">Book a 30-minute call with a Fleetroot specialist — choose a slot that suits you.</span>
              </span>
              <span className="inline-flex items-center gap-1 text-sm font-semibold text-[var(--fr-primary-dark)] whitespace-nowrap group-hover:gap-2 transition-[gap]">
                Schedule <ArrowRight size={15} />
              </span>
            </a>

            {/* Contact details block */}
            <div
              className="mt-12 pt-8 border-t border-[var(--fr-border)] divide-y divide-[var(--fr-border)] fade-up delay-4"
              data-testid="contact-details"
            >
              <div className="flex flex-wrap items-center justify-between gap-3 py-4">
                <div className="flex items-center gap-2 text-[15px] font-semibold">
                  <PhoneCall size={18} className="text-[var(--fr-primary-dark)]" /> Contact Us
                </div>
                <a
                  href={CONTACT.phoneHref}
                  className="font-display text-lg font-bold tracking-tight whitespace-nowrap underline decoration-[var(--fr-border)] underline-offset-4 hover:decoration-[var(--fr-primary)] hover:text-[var(--fr-primary-dark)] transition-colors"
                  data-testid="contact-phone-uae"
                >
                  {CONTACT.phone}
                </a>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3 py-4">
                <div className="flex items-center gap-2 text-[15px] font-semibold">
                  <Phone size={18} className="text-[var(--fr-primary-dark)]" /> For General Enquiries
                </div>
                <a
                  href={CONTACT.generalPhoneHref}
                  className="font-display text-lg font-bold tracking-tight whitespace-nowrap underline decoration-[var(--fr-border)] underline-offset-4 hover:decoration-[var(--fr-primary)] hover:text-[var(--fr-primary-dark)] transition-colors"
                  data-testid="contact-phone-us"
                >
                  {CONTACT.generalPhone}
                </a>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3 py-4">
                <div className="flex items-center gap-2 text-[15px] font-semibold">
                  <ThumbsUp size={18} className="text-[var(--fr-primary-dark)]" /> Follow Us On
                </div>
                <div className="flex items-center gap-2.5" data-testid="contact-socials">
                  {SOCIALS.map((s) => {
                    const Icon = Icons[s.icon] || Icons.Link;
                    return (
                      <a
                        key={s.name}
                        href={s.href}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={s.name}
                        title={s.name}
                        className="social-btn"
                        data-testid={`social-${s.name.toLowerCase()}`}
                      >
                        <Icon size={17} />
                      </a>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <Reveal className="card p-7 md:p-9">
            {submitted ? (
              <div className="text-center py-10" data-testid="contact-success">
                <CheckCircle2 size={48} className="mx-auto text-[var(--fr-primary)]" />
                <h2 className="font-display font-bold text-2xl mt-5">Thanks, {submitted.name.split(" ")[0]}!</h2>
                <p className="mt-3 text-[var(--fr-ink-2)]">
                  We’ve received your request for <strong>{submitted.company}</strong> ({submitted.fleetSize}). A specialist will
                  reply to <strong>{submitted.email}</strong> within 24 hours.
                </p>
                <button
                  type="button"
                  className="btn btn-outline mt-8"
                  onClick={() => {
                    setForm(initial);
                    setSubmitted(null);
                  }}
                  data-testid="contact-reset"
                >
                  Send another request
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} noValidate data-testid="contact-form">
                <h2 className="font-display font-bold text-2xl">Request a demo</h2>
                <p className="mt-1 text-sm text-[var(--fr-muted)]">30 minutes, tailored to your operation.</p>

                <div className="mt-7 grid sm:grid-cols-2 gap-4">
                  <Field label="Full name" error={errors.name}>
                    <input className="fr-input" placeholder="Jane Doe" value={form.name} onChange={set("name")} data-testid="contact-name" />
                  </Field>
                  <Field label="Work email" error={errors.email}>
                    <input className="fr-input" type="email" placeholder="jane@company.com" value={form.email} onChange={set("email")} data-testid="contact-email" />
                  </Field>
                  <Field label="Company" error={errors.company}>
                    <input className="fr-input" placeholder="Acme Logistics" value={form.company} onChange={set("company")} data-testid="contact-company" />
                  </Field>
                  <Field label="Fleet size" error={errors.fleetSize}>
                    <Select value={form.fleetSize} onValueChange={(v) => setForm((f) => ({ ...f, fleetSize: v }))}>
                      <SelectTrigger className="fr-input !h-[46px] rounded-xl" data-testid="contact-fleet-size">
                        <SelectValue placeholder="Select fleet size" />
                      </SelectTrigger>
                      <SelectContent>
                        {CONTACT.fleetSizes.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <div className="mt-4">
                  <Field label="What do you move? (optional)">
                    <textarea className="fr-input" rows={4} placeholder="e.g. 60 vans, same-day grocery in Dubai & Abu Dhabi, Odoo ERP" value={form.message} onChange={set("message")} data-testid="contact-message" />
                  </Field>
                </div>
                <button type="submit" className="btn btn-primary btn-lg w-full mt-7" disabled={loading} data-testid="contact-submit">
                  {loading ? "Sending…" : "Request a demo"}
                </button>
                <p className="mt-3 text-xs text-[var(--fr-muted)] text-center">We reply within 24h. No spam, ever.</p>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </>
  );
};

const Field = ({ label, error, children }) => (
  <label className="block">
    <span className="block text-sm font-medium mb-1.5">{label}</span>
    {children}
    {error && <span className="block mt-1 text-xs text-red-600">{error}</span>}
  </label>
);

export default Contact;
